import React from 'react';
import { Button, Checkbox, Popover, PopoverSurface, PopoverTrigger } from '@fluentui/react-components';
import { Info16Regular, Open16Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';

import { GuideTable, GuideTh, GuideThSort, guideCellClassName as cell, guideRowClassName } from '../../../theme/components/GuideTable';
import { GuidePagination } from '../../../theme/components/GuidePagination';
import { GuideTooltip } from '../../../theme/components/GuideTooltip';
import { GuideLinkButton } from '../../../theme/components/GuideButtons';
import { StatusPill } from '../../../theme/components/StatusPill';
import { StatusTone } from '../../../theme/guideTokens';
import {
  consumptionHasAlarm,
  consumptionStatusLabelKey,
  describeAlarm,
  describeValuation,
  formatMoney,
  isProviderFileImport,
  originLabelKey,
  originTone,
  PROVIDER_CONSUMPTIONS_PAGE_SIZES,
  protocolConsumptionLink,
  providerAssignmentStatus,
  rowProtocolLabel,
} from '../../../functions/ProviderConsumptions.Functions';
import { confidencePercent, formatImportDate, isLowConfidence } from '../../../functions/ProviderConsumptionImport.Functions';
import {
  ProviderConsumption,
  ProviderConsumptionsFilter,
  ProviderConsumptionSortKey,
} from '../../../models/Entities/ProviderConsumptions/ProviderConsumptions';
import { ImportAssignmentStatus } from '../../../models/Entities/ProviderConsumptionImport/ProviderConsumptionImport';
// Estado como círculo (US6): misma función y mismo CSS que la grilla de consumos del protocolo (sin duplicar colores).
import { GetRowClassByStatus, GetStatusDescription } from '../../Protocols/ConsumptionList/ConsumptionList.Functions';
import '../../Protocols/ConsumptionList/ConsumptionList.Styles.css';

const ASSIGNMENT_TONE: Record<ImportAssignmentStatus, StatusTone> = {
  Unassigned: 'neutral',
  Assigned: 'info',
  Generated: 'success',
};

export interface ProviderConsumptionsTableProps {
  items: ProviderConsumption[];
  /** La grilla llena el alto disponible con scroll propio y encabezado fijo (spec 008). */
  fill?: boolean;
  filter: ProviderConsumptionsFilter;
  total: number;
  onSort: (sortBy: ProviderConsumptionSortKey) => void;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  selected: string[];
  onToggle: (id: string) => void;
  /** Selecciona / deselecciona todas las filas visibles (spec 010: cualquier origen; cada acción toma su subconjunto). */
  onToggleAll: (ids: string[]) => void;
  onOpenConsumption: (item: ProviderConsumption) => void;
  /** Nombre del archivo de importación por id (secundario en la columna Servicio). */
  fileNameById: Map<string, string>;
  /** Abre el drawer de detalle del consumo (spec 010 US8): clic en la fila o Enter / Espacio con la fila enfocada. */
  onOpenDetail?: (item: ProviderConsumption) => void;
}

/** Elementos de la fila con acción, tooltip o callout propios: un clic en ellos no abre el detalle. */
const INTERACTIVE_SELECTOR =
  'a, button, input, select, textarea, label, [role="button"], [role="checkbox"], [role="link"], [role="menuitem"], [role="img"], [tabindex], [data-no-row-open]';

/**
 * El evento salió de un elemento interactivo de la fila (casilla, enlace, botón, callout, círculo de estado…) o de
 * fuera de ella (contenido de un popover en un portal, cuyo evento de React igual burbujea hasta la fila).
 */
export const isFromInteractiveElement = (target: EventTarget | null, row: HTMLElement): boolean => {
  if (!(target instanceof Element) || !row.contains(target)) return true;
  for (let node: Element | null = target; node && node !== row; node = node.parentElement) {
    if (node.matches(INTERACTIVE_SELECTOR)) return true;
  }
  return false;
};

/** Hay texto seleccionado (el usuario está copiando de la grilla): el clic no abre el detalle. */
const hasTextSelection = (): boolean => {
  try {
    return (window.getSelection?.()?.toString() ?? '').trim() !== '';
  } catch {
    return false;
  }
};

const Muted = () => <span className="pcs-muted">—</span>;

/** Grilla unificada (GuideTable): orden del servidor, una fila por consumo del proveedor de cualquier origen. */
export const ProviderConsumptionsTable = (props: ProviderConsumptionsTableProps) => {
  const { t } = useTranslation();
  const tr = (path: string, options?: Record<string, unknown>) => t(`providerConsumptions.${path}`, options);
  const { items, filter, selected } = props;

  const sort = (sortBy: ProviderConsumptionSortKey): GuideThSort => ({
    direction: filter.sortBy === sortBy ? filter.sortDirection ?? 'asc' : undefined,
    onSort: () => props.onSort(sortBy),
  });

  const selectableIds = items.map((item) => item.id);
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selected.includes(id));
  const someSelected = selectableIds.some((id) => selected.includes(id));

  const renderCost = (item: ProviderConsumption) => {
    const quantity = Number(item.quantity ?? 1);
    return (
      <>
        <span className="pcs-amount">{formatMoney(item.amount, item.currencyCode)}</span>
        {quantity !== 1 && item.unitAmount !== null && item.unitAmount !== undefined && (
          <span className="pcs-secondary">{tr('grid.quantityTimesUnit', { quantity, unit: formatMoney(item.unitAmount, item.currencyCode) })}</span>
        )}
        {isProviderFileImport(item) && (
          <span className="pcs-secondary" data-testid={`pcs-import-value-${item.id}`}>
            {item.importAmount === null || item.importAmount === undefined
              ? tr('grid.importNoValue')
              : tr('grid.importValue', { value: formatMoney(item.importAmount, item.importCurrencyCode) })}
          </span>
        )}
      </>
    );
  };

  const renderValuation = (item: ProviderConsumption) => {
    const valuation = describeValuation(item, t);
    return (
      <GuideTooltip content={valuation.tooltip} relationship="description">
        <span className="pcs-valuation" tabIndex={0} data-testid={`pcs-valuation-${item.id}`} data-source={valuation.source ?? 'none'}>
          <span className={valuation.source ? undefined : 'pcs-muted'}>{valuation.label}</span>
          <Info16Regular aria-hidden="true" className="pcs-valuation__icon" />
        </span>
      </GuideTooltip>
    );
  };

  const renderAlarm = (item: ProviderConsumption) => {
    const alarm = describeAlarm(item);
    if (!alarm) return <Muted />;
    // La pastilla es el disparador: el texto y el detalle de la alarma van en un callout.
    return (
      <div data-testid={`pcs-alarm-cell-${item.id}`}>
        <Popover withArrow positioning="below-start">
          <PopoverTrigger disableButtonEnhancement>
            <button type="button" className="pcs-alarm-trigger" aria-label={tr('alarm.open')} data-testid={`pcs-alarm-open-${item.id}`}>
              <StatusPill tone="warning">{tr('alarm.pill')}</StatusPill>
            </button>
          </PopoverTrigger>
          <PopoverSurface className="pcs-callout pcs-callout--alarm" data-testid={`pcs-alarm-surface-${item.id}`}>
            <span className="pcs-callout__title">{tr('alarm.pill')}</span>
            <span className="pcs-alarm-text">{alarm.text || tr('alarm.generic')}</span>
            {alarm.detail && <span className="pcs-secondary">{alarm.detail}</span>}
          </PopoverSurface>
        </Popover>
      </div>
    );
  };

  /** Círculo de color de la grilla de protocolo; la descripción (i18n) va en aria-label, tooltip y, en alto contraste, como texto. */
  const renderStatus = (item: ProviderConsumption) => {
    const label = t(consumptionStatusLabelKey(item.status));
    const status = item.status === null || item.status === undefined ? Number.NaN : Number(item.status);
    return (
      <span className="pcs-status" data-testid={`pcs-status-${item.id}`}>
        <GuideTooltip content={label} relationship="description">
          <span
            className={`status-mark ${GetStatusDescription(status).class}`}
            role="img"
            tabIndex={0}
            aria-label={label}
            data-testid={`pcs-status-mark-${item.id}`}
          />
        </GuideTooltip>
        <span className="status-mark-label" aria-hidden="true">
          {label}
        </span>
      </span>
    );
  };

  const renderRow = (item: ProviderConsumption) => {
    const isSelected = selected.includes(item.id);
    const assignment = providerAssignmentStatus(item);
    const link = protocolConsumptionLink(item);
    const service = item.serviceName || item.medicalServiceName || '—';
    const fileName = item.providerFileId !== undefined && item.providerFileId !== null ? props.fileNameById.get(String(item.providerFileId)) : undefined;
    const openDetail = props.onOpenDetail;
    const onRowClick = (event: React.MouseEvent<HTMLTableRowElement>) => {
      if (!openDetail || isFromInteractiveElement(event.target, event.currentTarget) || hasTextSelection()) return;
      openDetail(item);
    };
    const onRowKeyDown = (event: React.KeyboardEvent<HTMLTableRowElement>) => {
      // Sólo con el foco en la fila misma: Enter / Espacio en la casilla u otro control hacen lo suyo.
      if (!openDetail || event.target !== event.currentTarget) return;
      if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
        event.preventDefault();
        openDetail(item);
      }
    };
    return (
      <tr
        key={item.id}
        className={guideRowClassName(
          { selected: isSelected, pending: consumptionHasAlarm(item) && !isSelected },
          // Fila pintada según el estado, con la misma función y el mismo CSS que la grilla de protocolo (DEC-APP-021).
          [GetRowClassByStatus(Number(item.status)), openDetail ? 'pcs-row--clickable' : ''].filter(Boolean).join(' ') || undefined
        )}
        tabIndex={openDetail ? 0 : undefined}
        aria-label={openDetail ? tr('grid.openDetail', { service, date: formatImportDate(item.serviceDueDate) }) : undefined}
        onClick={openDetail ? onRowClick : undefined}
        onKeyDown={openDetail ? onRowKeyDown : undefined}
        data-testid={`pcs-row-${item.id}`}
      >
        {/* La celda entera de la casilla es de selección: un clic al lado de la casilla no abre el detalle. */}
        <td className={cell('check')} data-no-row-open="">
          <Checkbox
            checked={isSelected}
            onChange={() => props.onToggle(item.id)}
            aria-label={tr('grid.selectRow', { service, date: formatImportDate(item.serviceDueDate) })}
            data-testid={`pcs-select-${item.id}`}
          />
        </td>
        <td className="pcs-status-cell">{renderStatus(item)}</td>
        <td className="ichtys-num pcs-nowrap">{formatImportDate(item.serviceDueDate)}</td>
        <td>
          {/* Código y origen del archivo van en un callout: la grilla muestra sólo el servicio */}
          <span className="pcs-service">
            <span className="pcs-strong">{service}</span>
            {(item.medicalServiceCode || fileName || item.sourceReference) && (
              <Popover withArrow positioning="below-start">
                <PopoverTrigger disableButtonEnhancement>
                  <Button appearance="subtle" size="small" className="pcs-info" icon={<Info16Regular />} aria-label={tr('grid.moreInfo', { service })} title={tr('grid.moreInfoShort')} data-testid={`pcs-info-${item.id}`} />
                </PopoverTrigger>
                <PopoverSurface className="pcs-callout" data-testid={`pcs-info-surface-${item.id}`}>
                  <dl className="pcs-callout__facts">
                    {item.medicalServiceCode && (
                      <div className="pcs-callout__fact"><dt>{tr('grid.infoCode')}</dt><dd className="ichtys-num">{item.medicalServiceCode}</dd></div>
                    )}
                    {(fileName || item.sourceReference) && (
                      <div className="pcs-callout__fact">
                        <dt>{tr('grid.infoOrigin')}</dt>
                        <dd data-testid={`pcs-source-${item.id}`}>
                          {[
                            fileName,
                            item.sourceReference || (fileName && item.sourceRowNumber ? t('providerConsumptionImport.rowNumber', { row: item.sourceRowNumber }) : undefined),
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                        </dd>
                      </div>
                    )}
                  </dl>
                </PopoverSurface>
              </Popover>
            )}
          </span>
          {isLowConfidence(item.importConfidence) && (
            <StatusPill tone="warning" data-testid={`pcs-low-confidence-${item.id}`}>
              {tr('grid.lowConfidence', { value: confidencePercent(item.importConfidence) })}
            </StatusPill>
          )}
        </td>
        <td>
          {rowProtocolLabel(item) || <Muted />}
          {item.stageName && <span className="pcs-secondary">{item.stageName}</span>}
        </td>
        <td>
          {item.patientNumberInProtocol || <Muted />}
          {item.medicalRecordNumber && <span className="pcs-secondary">{tr('grid.medicalRecord', { value: item.medicalRecordNumber })}</span>}
          {!item.patientNumberInProtocol && item.patientReference && (
            <span className="pcs-secondary" data-testid={`pcs-patient-reference-${item.id}`}>
              {tr('grid.patientReference', { value: item.patientReference })}
            </span>
          )}
        </td>
        <td>{item.conceptDescription || (item.contractConceptId ? `#${item.contractConceptId}` : <Muted />)}</td>
        <td>
          <StatusPill tone={originTone(item.origin)} data-testid={`pcs-origin-${item.id}`}>
            {t(originLabelKey(item.origin))}
          </StatusPill>
        </td>
        <td className={cell('end', 'num')}>{renderCost(item)}</td>
        <td>{renderValuation(item)}</td>
        <td>{renderAlarm(item)}</td>
        <td>
          {assignment ? (
            <StatusPill tone={ASSIGNMENT_TONE[assignment]} data-testid={`pcs-assignment-${item.id}`}>
              {t(`providerConsumptionImport.status.${assignment}`)}
            </StatusPill>
          ) : (
            <Muted />
          )}
        </td>
        <td>
          {link ? (
            <GuideLinkButton
              size="small"
              icon={<Open16Regular />}
              onClick={(event) => {
                event.stopPropagation();
                props.onOpenConsumption(item);
              }}
              data-testid={`pcs-open-consumption-${item.id}`}
            >
              {tr('grid.viewProtocolConsumption')}
            </GuideLinkButton>
          ) : (
            <Muted />
          )}
        </td>
      </tr>
    );
  };

  return (
    <GuideTable
      ariaLabel={tr('section.title')}
      data-testid="pcs-table"
      fill={props.fill}
      scrollTestId="pcs-table-scroll"
      footer={
        <GuidePagination
          page={filter.pageNumber}
          pageSize={filter.pageSize}
          total={props.total}
          onPageChange={props.onPageChange}
          pageSizeOptions={PROVIDER_CONSUMPTIONS_PAGE_SIZES}
          onPageSizeChange={props.onPageSizeChange}
        />
      }
    >
      <thead>
        <tr>
          <th className={cell('check')} scope="col">
            {selectableIds.length > 0 && (
              <Checkbox
                checked={allSelected ? true : someSelected ? 'mixed' : false}
                onChange={() => props.onToggleAll(selectableIds)}
                aria-label={tr('grid.selectAll')}
                data-testid="pcs-select-all"
              />
            )}
          </th>
          <GuideTh sort={sort('status')}>{tr('columns.status')}</GuideTh>
          <GuideTh sort={sort('serviceDueDate')}>{tr('columns.date')}</GuideTh>
          <GuideTh sort={sort('serviceName')}>{tr('columns.service')}</GuideTh>
          <GuideTh sort={sort('protocolId')}>{tr('columns.protocol')}</GuideTh>
          <GuideTh sort={sort('patientNumberInProtocol')}>{tr('columns.patient')}</GuideTh>
          <GuideTh sort={sort('conceptDescription')}>{tr('columns.concept')}</GuideTh>
          <GuideTh sort={sort('origin')}>{tr('columns.origin')}</GuideTh>
          <GuideTh align="end" sort={sort('amount')}>
            {tr('columns.cost')}
          </GuideTh>
          <GuideTh>{tr('columns.valuation')}</GuideTh>
          <GuideTh sort={sort('settlementAlarmReason')}>{tr('columns.alarm')}</GuideTh>
          <GuideTh>{tr('columns.assignment')}</GuideTh>
          <GuideTh>{tr('columns.protocolConsumption')}</GuideTh>
        </tr>
      </thead>
      <tbody>{items.map(renderRow)}</tbody>
    </GuideTable>
  );
};

export default ProviderConsumptionsTable;
